import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { first } from 'lodash';

import { ibeSidebarAppointmentRead } from 'actions/appointment/beSidebarAppointment.action';
import { createDepositBookingVersion } from 'actions/payment/beCreateDeposit.action';
import feChangePaymentType from 'actions/payment/feChangePaymentType.action';
import beInitReaderPayment from 'actions/pos/beInitReaderPayment.action';
import feSetPosIntentId from 'actions/pos/feSetPosIntentId.action';
import ICO_FAILED from 'assets/images/dialogs/PaymentFailed.svg';
import apDepositUpdaterFor from 'bl/appointment/apDepositUpdaterFor.bl';
import { LT_DEPOSIT } from 'bl/loggers.bl';
import determinePaymentType from 'bl/payment/determinePaymentType.bl';
import determineAndStorePaymentIntentId from 'bl/pos/determineAndStorePaymentIntentId.bl';
import determinePriceLines from 'bl/pos/determinePriceLines.bl';
import DialogMessage from 'components/dialog/DialogMessage.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import DepositSpinner from 'components/generic/DepositSpinner.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import Space from 'components/generic/Space.component';
import PosReaderDialog from 'components/pos/PosReaderDialog.component';
import PRDS from 'components/pos/PosReaderDialogStatus.const';
import PosReaderPriceLine from 'components/pos/PosReaderPriceLine.component';
import PosStateMessage from 'components/pos/PosStateMessage.component';
import CurrencySign from 'components/price/CurrencySign.component';
import { USE_OLD_CARD_PAYMENT_ERROR } from 'config/use.conf';
import { CURRENCY_ACTIVE_BORDER_ICONS } from 'constants/currency.const';
import LINK from 'constants/link.const';
import EMSG from 'constants/messages/errorMessage.const';
import PAYMENT_ORIGIN_POS from 'constants/payment/paymentOriginPos.const';
import S from 'constants/string.const';
import { useCountryImage } from 'hooks/useCountryImage.hook';
import { useNavFor, useNavIsWeek } from 'hooks/useNav.hook';
import { usePathname } from 'hooks/usePathname.hook';
import { prepareReactQuery, usePrepareCacheDataUpdate } from 'hooks/usePrepare.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCheckoutData, selectCheckoutPaymentOption, selectCheckoutPos } from 'selectors/checkout.selectors';
import { selectCurrentSalonId, selectCurrentSalonPosFlow } from 'selectors/currentSalon.selectors';
import { selectServiceMap } from 'selectors/salonInfo.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import call from 'utils/functions/call.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import { cleanBookingData } from 'booking/actions';
import { cancelChargeSlickCardOnline, cleanTransactionData } from 'checkout/actions';
import AlertDialog from 'checkout/components/AlertDialog';
import ConfirmationDialogFromCheckout from 'checkout/components/ConfirmationDialog';
import PayPopUpPriceLine from 'checkout/components/PayPopUpPriceLine.component';

import CN from './PosDepositDialog.module.scss';

const CTAG = 'PosDepositDialog';
const L = logger([CTAG, LT_DEPOSIT]);

const sumDeposits = (depositDataList) => {
  let total = 0;
  for (const { use_deposit: d } of depositDataList) {
    total += d;
  }
  return total;
};

const sumVouchers = (usedVouchers) => {
  if (!usedVouchers) {
    return 0;
  }

  let total = 0;
  for (const voucher of usedVouchers) {
    if (voucher) {
      total += voucher.used_amount;
    }
  }

  return total;
};

const extractPaymentInfo = (props) => ({
  salonId: props?.salonId,
  appointmentId: first(props?.appointments),
  clientId: props?.data?.clients?.[0]?.id ?? void 1,
  chargeCard: props?.data?.onlineAmount > 0,
  voucherAmount: sumVouchers(props?.data?.vouchers_used),
  depositAmount: sumDeposits(props?.data?.depositListData),
  cardNumberEnd: fromAnyToTrimmed(props?.data?.active_card),
  cardAmount: props?.data?.cardAmount,
  cashAmount: props?.data?.partCash,
  otherAmount: props?.data?.otherAmount,
  onlineAmount: props?.data?.onlineAmount,
  total: props?.data?.total,
  items: [{ description: '', price: 0, quantity: 0 }],
});

const PosDepositDialog = (props) => {
  const { appointment, appointmentId, deposit, clientId, onClose, preSelectedReader } = props;

  const dispatch = useDispatch();

  const appointments = useSelector((state) => state.checkout.appointments);
  const cardOnFileChargeSuccess = useSelector((state) => state.checkout.cardOnFileChargeSuccess);
  const chargingCardError = useSelector((state) => state.checkout.chargingCardError);
  const checkoutOption = useSelector(selectCheckoutPaymentOption);
  const data = useSelector(selectCheckoutData);
  const pos = useSelector(selectCheckoutPos);
  const posFlow = useSelector(selectCurrentSalonPosFlow);
  const salonId = useSelector(selectCurrentSalonId);
  const serviceMap = useSelector(selectServiceMap);
  const transaction = useSelector((state) => state.transaction.create);

  const { stylistId } = useParams();
  const isWeek = useNavIsWeek();
  const pathname = usePathname();
  const pathSegments = pathname.split('/');
  const nav = useNavFor(CTAG);

  const [disableDuplicateTransaction, setDisableDuplicateTransaction] = useStateWhileMounted(false);
  const [errorMessageFromState, setErrorMessageFromState] = useStateWhileMounted('');
  const [confirmUpdateDialog, setConfirmUpdateDialog] = useStateWhileMounted(false);
  const [posStep, setPosStep] = useStateWhileMounted(null);
  const { val: isDepositLoaderVisible, yay: showLoader, nay: hideLoader } = useTip(false);
  const { val: isConfirmDisabled, yay: disableConfirm, nay: enableConfirm } = useTip(false);
  const { t } = useTranslation();
  const depositIcon = useCountryImage(CURRENCY_ACTIVE_BORDER_ICONS);

  const updateCacheData = usePrepareCacheDataUpdate({ tag: CTAG });

  const performTransaction = useCallback(
    async (paymentIntentId) => {
      const FTAG = 'performTransaction()';

      const isPosEnabled = posFlow === true; // NOTE: make sure POS is used only if posFLow is exactly true

      const cardAmount = data?.cardAmount;
      const onlineAmount = data?.onlineAmount;
      const paymentType = determinePaymentType({ checkoutOption, cardAmount, onlineAmount, isPosEnabled });

      if (transaction?.isFetching || disableDuplicateTransaction) return false;
      setDisableDuplicateTransaction(true);

      // NOTE: depositListData might be set each cycle near the end of the big loop and is a shared mutable state

      dispatch(feChangePaymentType({ paymentType }));
      // TODO: determine why this needs updated after both for cycles finish

      try {
        setErrorMessageFromState(S.empty);
        const result = await dispatch(
          createDepositBookingVersion(
            {
              clientId,
              amount: deposit?.amount,
              depositType: deposit?.depositType,
              notes: t('composite.posDepositDialog.notesDisabled'),
              created_for_appointmentId: appointmentId,
              source: deposit?.source,
              intent_id: paymentIntentId,
            },
            salonId,
          ),
        );

        setErrorMessageFromState(result.is.ok ? S.empty : t(EMSG.txCreateTransKey));

        const prep = prepareReactQuery({ configurator: ibeSidebarAppointmentRead, salonId, init: { appointmentId } });
        const queryKey = prep?.queryKey ?? asArray();

        if (result.is.ok) updateCacheData(queryKey, apDepositUpdaterFor(result));

        return result.is.ok;
      } catch (e) {
        L.warn(FTAG, e);

        enableConfirm();
        hideLoader();
        setErrorMessageFromState(t(EMSG.txCreateTransKey));

        throw e;
      }
    },
    [
      appointmentId,
      checkoutOption,
      clientId,
      dispatch,
      data?.cardAmount,
      data?.onlineAmount,
      deposit?.amount,
      deposit?.depositType,
      deposit?.source,
      disableDuplicateTransaction,
      enableConfirm,
      hideLoader,
      posFlow,
      salonId,
      setDisableDuplicateTransaction,
      setErrorMessageFromState,
      updateCacheData,
      transaction?.isFetching,
      t,
    ],
  );

  const cleanupAndNavigate = useCallback(() => {
    const dateString = pathSegments?.[4];

    hideLoader();
    dispatch(cleanBookingData());

    nav.push(isWeek ? linkTo({ pattern: LINK.scheduleWeek, params: { salonId, dateString, stylistId } }) : linkTo({ pattern: LINK.scheduleDay, params: { salonId, dateString } }));
  }, [dispatch, hideLoader, isWeek, nav, pathSegments, salonId, stylistId]);

  const confirm = useCallback(
    async (paymentIntentId) => {
      try {
        // NOTE: on abort, initTransaction will return false, and on error it will simply throw it
        return (await performTransaction(paymentIntentId)) && cleanupAndNavigate();
      } catch (e) {
        L.warn('confirm()', 'performTransaction error:', e);
      } finally {
        setErrorMessageFromState(S.empty);
      }
    },
    [cleanupAndNavigate, performTransaction, setErrorMessageFromState],
  );

  const closePopup = useCallback(() => {
    const { isFetching } = asObject(transaction);

    if (isFetching) return;
    if (posStep === PRDS.disconnected) return void setPosStep(PRDS.closed);

    dispatch(cleanBookingData());

    nav.push(
      isWeek
        ? linkTo({ pattern: LINK.scheduleWeek, params: { salonId, dateString: pathSegments[4], stylistId } })
        : linkTo({ pattern: LINK.scheduleDay, params: { salonId, dateString: pathSegments[4] } }),
    );
  }, [dispatch, isWeek, nav, pathSegments, posStep, salonId, setPosStep, stylistId, transaction]);

  const handleConfirm = useCallback(() => {
    const isPosTransaction = posFlow === true; // NOTE: make sure POS is used only if posFLow is exactly true

    if (data?.onlineAmount > 0) return void setConfirmUpdateDialog(true);
    if (!isPosTransaction || posStep === PRDS.success) return void L.unignore('handleConfirm()', 'confirm:', confirm());
    if (!posStep || posStep === PRDS.closed) return void setPosStep(PRDS.open);

    L.debug('handleConfirm()', 'Reached the end of the handler without triggering any action');
  }, [confirm, data?.onlineAmount, posFlow, posStep, setConfirmUpdateDialog, setPosStep]);

  const handleCancel = useCallback(() => {
    closePopup();
    call(`${CTAG}.onClose()`, onClose, { canceled: true });

    if (data?.onlineAmount > 0 || data?.cardAmount > 0) dispatch(cancelChargeSlickCardOnline());

    L.debug('handleCancel()', 'Reached the end of the handler without triggering any action');
  }, [dispatch, closePopup, data?.cardAmount, data?.onlineAmount, onClose]);

  const handlePosReaderDone = useCallback(
    (ev) => {
      if (ev?.canceled) handleCancel();

      if (ev?.success) {
        const promise = confirm(ev?.paymentIntentId);
        L.unignore('handlePosReaderDone()', 'confirm:', promise);
        promise.then((r) => L.debug('handlePosReaderDone() > then()', 'ev:', ev, 'result:', r));
        promise.then(() => call(`${CTAG}.onClose()`, onClose, ev));
      }

      L.debug('handlePosReaderDone()', 'Reached the end of the handler', ev);
    },
    [confirm, handleCancel, onClose],
  );

  const handlePosReaderChange = useCallback(
    (ev) => {
      L.debug('handlePosReaderChange()', ev);
      if (ev?.status === PRDS.success) {
        showLoader();
        disableConfirm();
      }
      setPosStep(ev?.status);
    },
    [disableConfirm, setPosStep, showLoader],
  );

  const handlePosReaderAction = useCallback(
    (ev) =>
      determineAndStorePaymentIntentId({
        readerId: ev?.readerId,
        pos,
        salonId,
        clientId,
        appointmentId: asObject(appointmentId),
        items: [
          {
            description: t('composite.posDepositDialog.depositDescription'),
            amount: deposit?.amount,
            quantity: 1,
          },
        ],
        amount: deposit?.amount,
        paymentOrigin: PAYMENT_ORIGIN_POS.deposit,
        beInitReaderPayment: (...args) => dispatch(beInitReaderPayment(...args)),
        feSetPosIntentId: (...args) => dispatch(feSetPosIntentId(...args)),
      }),
    [appointmentId, dispatch, clientId, deposit?.amount, pos, salonId, t],
  );

  const errorCallback = useCallback(() => {
    setErrorMessageFromState(S.empty);
    dispatch(cleanTransactionData());
    closePopup();
  }, [dispatch, closePopup, setErrorMessageFromState]);

  const cancelPayment = useCallback(() => void setConfirmUpdateDialog(false), [setConfirmUpdateDialog]);

  const { isFetching, errorMessage: errorMessageFromTransaction } = asObject(transaction);

  const { cardNumberEnd, onlineAmount } = extractPaymentInfo({ salonId, appointments, data });

  const isInPersonDialogOpen = USE_OLD_CARD_PAYMENT_ERROR && chargingCardError && cardOnFileChargeSuccess === false;
  // NOTE: allows for more customized error message to take precedance
  // SEE: https://getslick.atlassian.net/browse/GS-10878
  const errorMessage = isInPersonDialogOpen ? S.empty : errorMessageFromState || errorMessageFromTransaction;

  // NOTE: make sure POS is used only if posFLow is exactly true
  const isPosDialogOpen = posFlow === true && posStep && posStep !== PRDS.closed && posStep !== PRDS.cancel;

  return (
    <div data-bem="PosDepositDialog">
      {errorMessage && <AlertDialog title={errorMessage} buttonText="Close" onClick={errorCallback} onClose={errorCallback} />}

      {isDepositLoaderVisible && <DepositSpinner color="#3e0b6c" message={t('composite.posDepositDialog.finishingDeposit')} />}

      <PosReaderDialog
        open={isPosDialogOpen}
        onChange={handlePosReaderChange}
        onAction={handlePosReaderAction}
        onDone={handlePosReaderDone}
        price={deposit.amount}
        preSelectedReader={preSelectedReader}
      >
        <DialogMessage>
          <PosStateMessage posStep={posStep} price={deposit.amount} />
        </DialogMessage>

        {determinePriceLines({ data, appointment, serviceMap, t }).map(({ finalPrice, clientId, productId, serviceId, ...extra }) => (
          <PosReaderPriceLine {...extra} total={finalPrice} key={`${clientId}-${serviceId}-${productId}`} />
        ))}

        <PosReaderPriceLine description="Total" total={deposit.amount} />
      </PosReaderDialog>

      {isInPersonDialogOpen && (
        <ConfirmationDialogFromCheckout onRequestClose={handleCancel} title=" " leftButtonText="" rightButtonText={t('common.buttons.ok')} onRight={handleCancel} displayHeader="header-non">
          <div className="dialog-inner-title">{t('composite.posDepositDialog.chargeFailed')}</div>
          <img src={ICO_FAILED} alt="Client paid" className="dialog-image" />
          <div className="no-show-message center">{t(EMSG.chargeInPersonTransKey)}</div>
        </ConfirmationDialogFromCheckout>
      )}

      {confirmUpdateDialog && (
        <ConfirmationDialogFromCheckout
          title=""
          leftButtonText={t('common.button.cancelUpp')}
          showHeader={false}
          onLeft={cancelPayment}
          rightButtonText={t('common.button.confirmUpp')}
          onRight={() => void L.unignore('render()', 'confirm:', confirm())}
          onCancel={cancelPayment}
        >
          <div className="titelText">
            <Space>{t('composite.posDepositDialog.chargePart1')}</Space>
            <Space>
              <CurrencySign value={onlineAmount} />
            </Space>
            <Space>{t('composite.posDepositDialog.chargePart2')}</Space>
          </div>
          <div className="violetText">
            <Space>{t('composite.posDepositDialog.cardEnding')}</Space>
            <Space>{fromAnyToTrimmed(cardNumberEnd)}</Space>
          </div>

          <div className="redText">{t('composite.posDepositDialog.chargeNote')}</div>
        </ConfirmationDialogFromCheckout>
      )}

      {!isConfirmDisabled && !isDepositLoaderVisible && (
        <SalonModal tag="PosDepositDialog__modal" isOpen withFooter>
          <SalonModal.Body tag="PosDepositDialog__modal-body">
            <div className={CN.image}>
              <IconStatus variant="alert">
                <Icon variant="alert" />
              </IconStatus>
            </div>
            <div className={CN.content}>
              <div className={CN.title}>
                <Space>{t('composite.posDepositDialog.confirmTitle')}</Space>
                <CurrencySign value={deposit.amount} />
              </div>
              <span className={CN.text}>{deposit && <PayPopUpPriceLine icon={depositIcon} description={t('composite.posDepositDialog.deposit')} value={deposit.amount} />}</span>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer tag="PosDepositDialog__modal-footer">
            <Button tag="PosDepositDialog__modal-cancel-button" variant="slim" onClick={handleCancel}>
              {t('common.buttons.cancelUpp')}
            </Button>

            {isFetching ? (
              <Button tag="PosDepositDialog__modal-loading-text" variant="slim">
                {t('common.loading')}
              </Button>
            ) : (
              <Button tag="PosDepositDialog__modal-confirm-button" variant="primaryForce" onClick={handleConfirm} disabled={isConfirmDisabled}>
                {t('common.buttons.confirmUpp')}
              </Button>
            )}
          </SalonModal.Footer>
        </SalonModal>
      )}
    </div>
  );
};

export default PosDepositDialog;
